import BdrModal from "../../../../components/BdrModal/BdrModal";
import BdrButton from "../../../../components/BdrButton/BdrButton";
import BdrCheckbox from "../../../../components/BdrCheckbox/BdrCheckbox";
import "./placeholderCleanupModal.css";
import React, { useEffect, useState } from "react";
import { useLazyQuery, useMutation } from "@apollo/client";
import { GET_CLIENT_PLACEHOLDERS } from "../../../../utils/GraphQL/queries";
import { DELETE_CALENDAR_ITEMS } from "../../../../utils/GraphQL/mutations";
import { useSnackbar } from "../../../../utils/GlobalSnackbar/GlobalSnackbarContext";
import BAGroupTimeFormat from "../../../../utils/formatters/BAGroupTimeFormat";
import { getFinisherRange } from "../../../../utils/calendarSplitBetween";

// Shown after a customer's event gains a quote/invoice (create-with-doc,
// attach-via-edit, or quote->invoice graduation). Lists that customer's OTHER
// true placeholders (job, isPlaceholder:true) so the office can clear the
// now-superseded tentative dates. `excludeEventId` drops the event just acted
// on. If the customer has no other placeholders, the modal self-closes with no
// visible UI (so hosts can fire it unconditionally on every doc-attach).
const PlaceholderCleanupModal = ({
  open,
  clientId,
  excludeEventId,
  onClose,
  onCleared,
}) => {
  const { handleSnack } = useSnackbar();
  const [getClientPlaceholders] = useLazyQuery(GET_CLIENT_PLACEHOLDERS);
  const [deleteCalendarItems] = useMutation(DELETE_CALENDAR_ITEMS);
  const [loading, setLoading] = useState(true);
  const [placeholders, setPlaceholders] = useState([]);
  const [checked, setChecked] = useState(() => new Set());
  const [clearing, setClearing] = useState(false);

  useEffect(() => {
    if (!open || !clientId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setChecked(new Set());
      try {
        const { data } = await getClientPlaceholders({
          variables: { clientId },
          fetchPolicy: "network-only",
        });
        if (cancelled) return;
        const all = (data?.getClientPlaceholders || []).filter(
          (p) => p?.calendarItem?.id && p.calendarItem.id !== excludeEventId
        );
        setPlaceholders(all);
        // Nothing else tentative for this customer -> close silently.
        if (all.length === 0) {
          setLoading(false);
          onClose?.();
          return;
        }
      } catch (err) {
        console.error(
          "[PlaceholderCleanup] load failed:",
          err?.message || err
        );
        if (!cancelled) {
          setLoading(false);
          onClose?.();
          return;
        }
      }
      if (!cancelled) setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, clientId, excludeEventId]);

  const toggle = (id) => {
    setChecked((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const allChecked =
    placeholders.length > 0 && checked.size === placeholders.length;

  const toggleAll = () => {
    setChecked(
      allChecked
        ? new Set()
        : new Set(placeholders.map((p) => p.calendarItem.id))
    );
  };

  const handleClear = async () => {
    const ids = [...checked];
    if (ids.length === 0) {
      onClose?.();
      return;
    }
    setClearing(true);
    try {
      const { data } = await deleteCalendarItems({
        variables: { ids, clientId },
      });
      const count = data?.deleteCalendarItems ?? 0;
      handleSnack(
        `Cleared ${count} tentative date${count === 1 ? "" : "s"}`,
        2500
      );
      onCleared?.();
    } catch (err) {
      console.error(
        "[PlaceholderCleanup] clear failed:",
        err?.message || err
      );
      handleSnack("Failed to clear tentative dates", 3000);
    }
    setClearing(false);
    onClose?.();
  };

  // While loading (brief) or when there's nothing to clean (self-closing),
  // render nothing — no empty-flash before the silent close.
  if (!open || loading || placeholders.length === 0) return null;

  const fmtDate = (ms) => BAGroupTimeFormat(ms, true, true);
  const assigneeNames = (p) =>
    (p.assignedUsers || [])
      .map((u) => `${u.firstName || ""} ${u.lastName || ""}`.trim())
      .filter(Boolean)
      .join(", ");
  const notesOf = (p) => {
    const pn = p.calendarItem?.priorityNotes || [];
    const n = p.calendarItem?.note;
    return [...pn, ...(n ? [n] : [])].join(" · ");
  };
  const customerName = placeholders[0]?.customer
    ? `${placeholders[0].customer.firstName || ""} ${
        placeholders[0].customer.lastName || ""
      }`.trim()
    : "This customer";

  return (
    <BdrModal
      open={open}
      onClose={clearing ? () => {} : onClose}
      closeOnBackdrop={!clearing}
      closeOnEsc={!clearing}
      title="Clear other tentative dates?"
      variant="panel"
      size="md"
      /* top: every host that opens this already has a modal up — the create
         and edit event modals, and the quote overlay. */
      layer="top"
      actions={
        <>
          <BdrButton disabled={clearing} onClick={onClose}>
            Keep all
          </BdrButton>
          <BdrButton
            variant="danger"
            confirm={false}
            disabled={clearing || checked.size === 0}
            onClick={handleClear}
          >
            {clearing ? "Clearing..." : `Clear ${checked.size} selected`}
          </BdrButton>
        </>
      }
    >
      <p className="phc-lead">
        {customerName} still has {placeholders.length} open tentative
        placeholder{placeholders.length === 1 ? "" : "s"}. Select the ones to
        clear (this permanently deletes those calendar holds).
      </p>
      {/* `indeterminate` is the point of a select-all: some-but-not-all is a
          third state, and showing it as simply unchecked loses the fact that
          a selection exists at all. */}
      <BdrCheckbox
        className="phc-select-all"
        label="Select all"
        checked={allChecked}
        indeterminate={checked.size > 0 && !allChecked}
        onChange={toggleAll}
      />
      <div className="phc-list">
          {placeholders.map((p) => {
            const id = p.calendarItem.id;
            const names = assigneeNames(p);
            const notes = notesOf(p);
            // Placeholder Min/Max (2026-10-29): the row is a true placeholder
            // (invoice/quote are null here), so this reads the item's own number.
            const finishers = getFinisherRange(p);
            return (
              <BdrCheckbox
                key={id}
                className="phc-item"
                checked={checked.has(id)}
                onChange={() => toggle(id)}
                /* Spans, not divs: the whole control is a <label>, whose
                   content model is phrasing. The two classes below carry
                   `display: block` so they still stack. */
                label={
                  <>
                    <span className="phc-item-date">
                      {fmtDate(p.calendarItem.startMs)}
                    </span>
                    {names && (
                      <span className="phc-item-meta">Assigned: {names}</span>
                    )}
                    {finishers && (
                      <span className="phc-item-meta">Refinishers: {finishers}</span>
                    )}
                    {notes && <span className="phc-item-meta">{notes}</span>}
                  </>
                }
              />
            );
          })}
      </div>
    </BdrModal>
  );
};

export default PlaceholderCleanupModal;
